import { Fragment, type UIEvent } from "react";
import { Loader2 } from "lucide-react";
import type { DrillColumn, DrillThroughClientGroup, DrillThroughRow } from "@/types/drillThroughTable";
import {
  formatCurrency,
  formatText,
  getCellValue,
  getColumnWidth,
  getTableMinWidth,
  getRowKey,
} from "@/lib/drillThroughTableNormalizers";
import { getAriaSort, SortableColumnHeader } from "@/components/SortableColumnHeader";
import type { SortDir, SortState } from "@/lib/tableSort";

type DrillThroughDataTableProps = {
  title: string;
  rows: DrillThroughRow[] | DrillThroughClientGroup[];
  columns: DrillColumn[];
  loading?: boolean;
  loadingMessage?: string;
  /** True while re-fetching this table (sort/filter change) with data already showing -- rows stay visible, dimmed, instead of blanking to a skeleton. */
  refetching?: boolean;
  /** Shown in place of rows only when there are none to show; a failed refetch leaves previously-loaded rows visible instead. */
  error?: string | null;
  groupByClient?: boolean;
  totalRows?: number;
  /** Noun used in the "X / Y {noun}" count label, e.g. "clients". Defaults to "rows". */
  countNoun?: string;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  sort?: SortState;
  onSort?: (key: string, direction: SortDir | null) => void;
};

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

// Approximate row height used to convert the "prefetch when N rows from the
// bottom" rule into a scroll-distance threshold.
const ROW_HEIGHT_PX = 40;
const PREFETCH_ROWS_FROM_BOTTOM = 10;

const AGE_BAND_CELL_COLORS: Record<string, string> = {
  age_0_30: "bg-[#2f8f9d]/75 text-slate-950",
  age_31_60: "bg-[#78b760]/75 text-slate-950",
  age_61_90: "bg-[#d7bd45]/80 text-slate-950",
  age_91_120: "bg-[#d99355]/75 text-slate-950",
  age_120_180: "bg-[#cf777f]/75 text-slate-950",
  age_180_plus: "bg-[#b84f55]/80 text-white",
};

function hasCellContent(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

function getAgeBandCellColor(columnKey: string, value: unknown): string | false {
  return hasCellContent(value) && AGE_BAND_CELL_COLORS[columnKey];
}

export function DrillThroughDataTable({
  title,
  rows,
  columns,
  loading = false,
  loadingMessage,
  refetching = false,
  error = null,
  groupByClient = false,
  totalRows,
  countNoun = "rows",
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  sort,
  onSort,
}: DrillThroughDataTableProps) {
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = event.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= PREFETCH_ROWS_FROM_BOTTOM * ROW_HEIGHT_PX) {
      onLoadMore();
    }
  };

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-label={title}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold text-card-foreground">{title}</h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          {refetching ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
          {loading ? "Loading" : totalRows !== undefined ? `${rows.length} / ${totalRows} ${countNoun}` : `${rows.length} ${countNoun}`}
        </span>
      </div>

      <div
        className={cx(
          "relative max-h-[calc(64vh-5rem)] min-h-[340px] overflow-auto",
          refetching && "opacity-60 transition-opacity",
        )}
        onScroll={handleScroll}
      >
        {loading && loadingMessage ? (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-card/60">
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingMessage}
            </div>
          </div>
        ) : null}

        <table
          className="w-full table-fixed border-separate border-spacing-0 text-xs"
          style={{
            minWidth: getTableMinWidth(
              columns.filter((column) => column.kind === "text").length,
              columns.filter((column) => column.kind !== "text").length,
            ),
          }}
        >
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: getColumnWidth(column, columns) }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column, columnIndex) => (
                <th
                  key={column.key}
                  className={cx(
                    "sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 font-bold leading-tight text-accent-foreground",
                    columnIndex === 0 && "left-0 z-30",
                    column.kind === "money" ? "text-center" : "text-left",
                  )}
                  aria-sort={getAriaSort(column.sortKey, sort)}
                >
                  <SortableColumnHeader
                    label={column.label}
                    sortKey={column.sortKey}
                    sortType={column.sortType}
                    sort={sort}
                    onSort={onSort}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRows columns={columns} />
            ) : rows.length === 0 && error ? (
              <tr>
                <td className="h-44 bg-card text-center text-destructive" colSpan={columns.length}>
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="h-44 bg-card text-center text-muted-foreground" colSpan={columns.length}>
                  No records found
                </td>
              </tr>
            ) : groupByClient ? (
              <GroupedClientRows rows={rows as DrillThroughClientGroup[]} columns={columns} />
            ) : (
              <FlatRows rows={rows as DrillThroughRow[]} columns={columns} />
            )}
            {!loading && loadingMore ? (
              <tr>
                <td className="border-b bg-card px-3 py-2.5 text-center text-muted-foreground" colSpan={columns.length}>
                  <span className="inline-flex items-center gap-2 text-xs">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading more...
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FlatRows({
  rows,
  columns,
}: {
  rows: DrillThroughRow[];
  columns: DrillColumn[];
}) {
  return (
    <>
      {rows.map((row, rowIndex) => (
        <tr key={getRowKey(row, rowIndex)} className="even:bg-muted/35 hover:bg-secondary/50">
          {columns.map((column, columnIndex) => {
            const value = getCellValue(row, column.key);
            const isMoney = column.kind === "money";
            return (
              <td
                key={column.key}
                className={cx(
                  "border-b border-r px-3 py-2.5 text-foreground",
                  isMoney ? "whitespace-normal break-words text-center tabular-nums" : "whitespace-normal break-words font-medium leading-snug",
                  columnIndex === 0 && "sticky left-0 z-10 bg-card shadow-[1px_0_0_hsl(var(--border))]",
                  columnIndex === 0 && rowIndex % 2 === 1 && "bg-muted",
                  column.key === "total" && "font-bold",
                  isMoney && getAgeBandCellColor(column.key, value),
                )}
              >
                {isMoney ? formatCurrency(value, 0) : formatText(value)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function GroupedClientRows({
  rows,
  columns,
}: {
  rows: DrillThroughClientGroup[];
  columns: DrillColumn[];
}) {
  const valueColumns = columns.slice(2);

  return (
    <>
      {rows.map((group, groupIndex) => {
        const assignments = group.assignments ?? [];
        const groupKey = String(group.client_code ?? group.client_name ?? groupIndex);
        const shaded = groupIndex % 2 === 1;
        const clientRowSpan = assignments.length + 1;
        const clientNameCell = (
          <td
            rowSpan={clientRowSpan}
            className={cx(
              "sticky left-0 z-10 border-b border-r bg-card px-3 py-2.5 align-top font-semibold leading-snug text-foreground shadow-[1px_0_0_hsl(var(--border))]",
              shaded && "bg-muted",
            )}
          >
            {formatText(group.client_name)}
          </td>
        );

        return (
          <Fragment key={groupKey}>
            {assignments.map((assignment, assignmentIndex) => (
              <tr
                key={`${groupKey}-${assignment.assignment_code ?? assignment.assignment_name ?? assignmentIndex}`}
                className={cx("hover:bg-secondary/50", shaded && "bg-muted/35")}
              >
                {assignmentIndex === 0 ? clientNameCell : null}
                <td className="border-b border-r px-3 py-2.5 font-medium leading-snug text-foreground">
                  {formatText(getCellValue(assignment, "assignment_name"))}
                </td>
                {valueColumns.map((column) => {
                  const value = getCellValue(assignment, column.key);
                  return (
                    <td
                      key={column.key}
                      className={cx(
                        "border-b border-r px-3 py-2.5 text-center tabular-nums text-foreground",
                        column.key === "total" && "font-bold",
                        getAgeBandCellColor(column.key, value),
                      )}
                    >
                      {formatCurrency(value, 0)}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className={cx("hover:bg-secondary/50", shaded && "bg-muted/35")}>
              {assignments.length === 0 ? clientNameCell : null}
              <td className="border-b border-r px-3 py-2.5 font-bold leading-snug text-foreground">
                {formatText(group.total?.label ?? "Total")}
              </td>
              {valueColumns.map((column) => {
                const value = getCellValue(group.total, column.key);
                return (
                  <td
                    key={column.key}
                    className={cx(
                      "border-b border-r px-3 py-2.5 text-center font-bold tabular-nums text-foreground",
                      getAgeBandCellColor(column.key, value),
                    )}
                  >
                    {formatCurrency(value, 0)}
                  </td>
                );
              })}
            </tr>
          </Fragment>
        );
      })}
    </>
  );
}

function LoadingRows({ columns }: { columns: DrillColumn[] }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, rowIndex) => (
        <tr key={rowIndex} className="even:bg-muted/35">
          {columns.map((column, columnIndex) => (
            <td
              key={column.key}
              className={cx(
                "border-b border-r px-3 py-2.5",
                columnIndex === 0 && "sticky left-0 z-10 bg-card shadow-[1px_0_0_hsl(var(--border))]",
                columnIndex === 0 && rowIndex % 2 === 1 && "bg-muted",
              )}
            >
              <div
                className={cx(
                  "h-4 animate-pulse rounded bg-muted",
                  column.kind === "text" ? "w-32" : "mx-auto w-20",
                )}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
